import { ensureSubsetOfKeys } from '@daymade/common-lib/types';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ZPrizeSelect, ZProductSelect } from '../prize/models';
import { booleanParamSchema, successSchema } from '../utils';
import {
  ZDrawSelect,
  ZDrawTypeSelect,
  ZPlayerSelect,
  ZPrizeOrderInsert,
  ZPrizeOrderProductInsert,
  ZPrizeOrderProductSelect,
  ZPrizeOrderSelect,
  ZPrizeWinInsert,
  ZPrizeWinSelect,
  ZSubmitPrizeChoiceRequest,
  ZTicketEntrySelect,
} from './models';

const ZGetPrizeWinsResponse = ZPrizeWinSelect.extend({
  drawId: z.number(),
  drawName: z.string(),
  drawTypeName: z.string().nullable(),
  drawTypeGroup: z.string().nullable(),
  gameType: z.string(),
  entry: ZTicketEntrySelect.shape.entry,
  drawResult: ZDrawSelect.shape.result.nullable(),
  prizeStructureCode: z.string(),
  prizeTierCode: z.string(),
  prizeTierType: z.string(),
  prizeTierValue: z.number().nullable(),
  playerId: z.string(),
  playerName: z.string(),
  playerEmailAddress: z.string(),
  playerPhoneNumber: z.string().nullable(),
  chosenPrizeName: z.string().nullable(),
  chosenPrizeDescription: z.string().nullable(),
  assigneeName: z.string().nullable(),
  prizeOrder: ZPrizeOrderSelect.nullable(),
  products: ZPrizeOrderProductSelect.merge(
    ZProductSelect.pick({
      name: true,
      price: true,
      productCode: true,
      discountCode: true,
      purchaseFrom: true,
    }),
  ).array(),
}).array();
export type TGetPrizeWinsResponse = z.infer<typeof ZGetPrizeWinsResponse>;

const ZUpdatePrizeWinRequest = ZPrizeWinInsert.pick({
  id: true,
  notes: true,
  expiresAt: true,
}).required({
  id: true,
});
const ZUpdatePrizeWinResponse = ZPrizeWinSelect;
export type TUpdatePrizeWinRequest = z.infer<typeof ZUpdatePrizeWinRequest>;
export type TUpdatePrizeWinResponse = z.infer<typeof ZUpdatePrizeWinResponse>;

const ZGetPrizeOrdersResponse = ZPrizeOrderSelect.extend({
  prizeName: z.string(),
  prizeInternalInfo: z.record(z.string(), z.string()),
  brand: z.string().nullable(),
  drawId: z.number().nullable(),
  gameType: ZDrawTypeSelect.shape.gameType.nullable(),
  drawTypeGroup: z.string().nullable(),
  prizeStructureCode: z.string().nullable(), // TBC: nullable for token redemption?
  prizeTierCode: z.string().nullable(), // TBC: nullable for token redemption?
  tokenRedemptionId: z.string().nullable(),
  tokenRedemptionOptionTitle: z.string().nullable(),
  playerName: z.string(),
  playerEmail: z.string(),
  assigneeName: z.string().nullable(),
  notes: z.string().nullable(),
  products: ZPrizeOrderProductSelect.merge(
    ZProductSelect.pick({
      name: true,
      price: true,
      productCode: true,
      discountCode: true,
      purchaseFrom: true,
    }),
  ).array(),
}).array();
export type TGetPrizeOrdersResponse = z.infer<typeof ZGetPrizeOrdersResponse>;

const ZUpdatePrizeOrderRequest = ZPrizeOrderInsert.pick({
  id: true,
  status: true,
  assigneeId: true,
  deliveryStreet: true,
  deliveryCity: true,
  deliveryPostcode: true,
  emailAddress: true,
  phoneNumber: true,
  additionalInfoFromPlayer: true,
})
  .required({
    id: true,
  })
  .partial({
    additionalInfoFromPlayer: true,
  });
const ZUpdatePrizeOrderResponse = z.object({
  prizeOrder: ZPrizeOrderSelect,
  prizeOrderProducts: ZPrizeOrderProductSelect.array().optional(),
});
export type TUpdatePrizeOrderRequest = z.infer<typeof ZUpdatePrizeOrderRequest>;
export type TUpdatePrizeOrderResponse = z.infer<
  typeof ZUpdatePrizeOrderResponse
>;

const ZUpdatePrizeOrderProductRequest = ZPrizeOrderProductInsert.pick({
  id: true,
  status: true,
  orderNumber: true,
  trackingNumber: true,
}).required({
  id: true,
});
const ZUpdatePrizeOrderProductResponse = z.object({
  prizeOrderProduct: ZPrizeOrderProductSelect,
  prizeOrder: ZPrizeOrderSelect.optional(),
});
export type TUpdatePrizeOrderProductRequest = z.infer<
  typeof ZUpdatePrizeOrderProductRequest
>;
export type TUpdatePrizeOrderProductResponse = z.infer<
  typeof ZUpdatePrizeOrderProductResponse
>;

const ZGetPrizeOrderProductsResponse = ZPrizeOrderSelect.pick({
  prizeWinId: true,
  deliveryStreet: true,
  deliveryCity: true,
  deliveryPostcode: true,
  emailAddress: true,
  phoneNumber: true,
  additionalInfoFromPlayer: true,
  assigneeId: true,
})
  .merge(
    ZProductSelect.pick({
      price: true,
      productCode: true,
      discountCode: true,
      purchaseFrom: true,
      orderForm: true,
    }),
  )
  .extend({
    createdAt: z.coerce.date(),
    prizeOrderId: z.string(),
    prizeOrderStatus: ZPrizeOrderSelect.shape.status,
    prizeName: z.string(),
    prizeType: ZPrizeSelect.shape.type,
    prizeInternalInfo: z.record(z.string(), z.string()),
    brand: z.string().nullable(),
    drawId: z.number().nullable(),
    gameType: ZDrawTypeSelect.shape.gameType.nullable(),
    drawTypeGroup: z.string().nullable(),
    prizeStructureCode: z.string().nullable(), // TBC: nullable for token redemption?
    prizeTierCode: z.string().nullable(), // TBC: nullable for token redemption?
    tokenRedemptionId: z.string().nullable(),
    tokenRedemptionOptionTitle: z.string().nullable(),
    promoPrizeRewardId: z.string().nullable(),
    promoPrizeOfferDescription: z.string().nullable(),
    playerName: z.string(),
    playerFirstName: z.string(),
    playerLastName: z.string(),
    playerEmail: z.string(),
    assigneeName: z.string().nullable(),
    notes: z.string().nullable(),
    prizeOrderProductId: z.string(),
    prizeOrderProductStatus: ZPrizeOrderProductSelect.shape.status,
    orderNumber: z.string().nullable(),
    trackingNumber: z.string().nullable(),
    productName: z.string(),
    internalProductName: z.string().nullable(),
  })
  .array();
export type TGetPrizeOrderProductsResponse = z.infer<
  typeof ZGetPrizeOrderProductsResponse
>;

const ZGetDrawsWithPrizeWinsResponse = ZDrawSelect.pick({
  id: true,
  name: true,
})
  .extend(
    ensureSubsetOfKeys(ZDrawSelect.shape, {
      drawDate: z.coerce.date().nullable(),
    }),
  )
  .extend({
    gameType: ZDrawTypeSelect.shape.gameType,
    drawTypeGroup: ZDrawTypeSelect.shape.group,
  })
  .array();
export type TGetDrawsWithPrizeWinsResponse = z.infer<
  typeof ZGetDrawsWithPrizeWinsResponse
>;

const ZUnsetPrizeChoiceRequest = z.object({
  prizeWinId: z.string(),
});
const ZUnsetPrizeChoiceResponse = z.object({
  prizeWin: ZPrizeWinSelect.optional(),
  cancelledPrizeOrder: ZPrizeOrderSelect.optional(),
});
const ZUnsetPrizeChoiceBadRequestResponse = z.object({
  message: z.string(),
  reason: z
    .union([
      z.literal('PRIZE_TYPE_BUNDLE'),
      z.literal('GIFTCLOUD_GIFT_CARD'),
      z.literal('PRIZE_ORDER_ALREADY_CANCELLED'),
      z.literal('PRIZE_ORDER_PRODUCT_ALREADY_CANCELLED'),
    ])
    .optional(),
});
export type TUnsetPrizeChoiceRequest = z.infer<typeof ZUnsetPrizeChoiceRequest>;
export type TUnsetPrizeChoiceResponse = z.infer<
  typeof ZUnsetPrizeChoiceResponse
>;
const ZUnsetPrizeChoicesRequest = z.object({
  prizeWinIds: z.string().array(),
});
const ZUnsetPrizeChoicesResponse = z.object({
  success: z.literal(true),
});

const ZAdminPrizeChoiceRequest = ZSubmitPrizeChoiceRequest.extend({
  customPrizeName: z.string().optional(),
});

const ZGetWinnerManagerResponse = ZPlayerSelect.pick({
  id: true,
  firstName: true,
}).array();
export type TGetWinnerManagerResponse = z.infer<
  typeof ZGetWinnerManagerResponse
>;

const c = initContract();

export const adminWinnerContract = c.router(
  {
    getPrizeWinsForPlayer: {
      method: 'GET',
      path: '/player/:playerId',
      pathParams: z.object({
        playerId: z.string(),
      }),
      responses: {
        200: ZGetPrizeWinsResponse,
      },
      summary: 'Get prize wins of a draw',
    },

    getPrizeWinsOfDraw: {
      method: 'GET',
      path: '/get-prize-wins-of-draw',
      query: z.object({
        drawId: z.coerce.number(),
      }),
      responses: {
        200: ZGetPrizeWinsResponse,
      },
      summary: 'Get prize wins of a draw',
    },

    getPrizeWinsOfAllRaffleDraws: {
      method: 'GET',
      path: '/get-prize-wins-of-all-raffle-draws',
      responses: {
        200: ZGetPrizeWinsResponse,
      },
      summary: 'Get prize wins of all raffle draws',
    },

    getPrizeWinsOfAllMinilottoDraws: {
      method: 'GET',
      path: '/get-prize-wins-of-all-minilotto-draws',
      responses: {
        200: ZGetPrizeWinsResponse,
      },
      summary: 'Get prize wins of all minilotto draws',
    },

    getPrizeWinsAwardedHighValued: {
      method: 'GET',
      path: '/get-prize-wins-awarded-high-valued',
      responses: {
        200: ZGetPrizeWinsResponse,
      },
      summary: 'Get awarded high-valued prize wins of all lottery draws',
    },

    updatePrizeWin: {
      method: 'PATCH',
      path: '/update-prize-win',
      body: ZUpdatePrizeWinRequest,
      responses: {
        200: ZUpdatePrizeWinResponse,
        400: c.type<{ message: string }>(),
      },
    },

    getDrawsWithPrizeWins: {
      method: 'GET',
      path: '/get-draws-with-prize-wins',
      responses: {
        200: ZGetDrawsWithPrizeWinsResponse,
      },
      summary: 'Get IDs and names of draws with prize wins',
    },

    getPrizeOrdersUnfulfilledRecent: {
      method: 'GET',
      path: '/get-prize-orders-unfulfilled-recent',
      responses: {
        200: ZGetPrizeOrdersResponse,
      },
      summary: 'Get unfufilled and recently fulfilled prize orders',
    },

    getTokenRedeemedPrizesOfPlayer: {
      method: 'GET',
      path: '/player/:playerId/token-redeemed-prizes',
      responses: {
        200: ZGetPrizeOrdersResponse,
      },
      summary: 'Get token redeemed prizes of a player',
    },

    updatePrizeOrder: {
      method: 'PATCH',
      path: '/update-prize-order',
      body: ZUpdatePrizeOrderRequest,
      responses: {
        200: ZUpdatePrizeOrderResponse,
        400: c.type<{ message: string }>(),
      },
    },

    markPrizeOrdersAsConfirmed: {
      method: 'POST',
      path: '/mark-prize-orders-as-confirmed',
      body: z.string().array().min(1),
      responses: {
        200: successSchema,
      },
      summary: 'Mark prize orders as confirmed in batch',
    },

    markPrizeOrderProductsAsConfirmed: {
      method: 'POST',
      path: '/mark-prize-order-products-as-confirmed',
      body: z.string().array().min(1),
      responses: {
        200: successSchema,
      },
      summary: 'Mark prize order products as confirmed in batch',
    },

    getPrizeOrderProducts: {
      method: 'GET',
      path: '/prize-order-products',
      query: z.object({
        orderForm: booleanParamSchema.optional(),
        treatWins: booleanParamSchema.optional(),
      }),
      responses: {
        200: ZGetPrizeOrderProductsResponse,
      },
      summary: 'Get unfulfilled and recently fulfilled prize order products',
    },

    updatePrizeOrderProduct: {
      method: 'PATCH',
      path: '/update-prize-order-product',
      body: ZUpdatePrizeOrderProductRequest,
      responses: {
        200: ZUpdatePrizeOrderProductResponse,
        400: c.type<{ message: string }>(),
      },
    },

    unsetPrizeChoice: {
      method: 'POST',
      path: '/unset-prize-choice',
      body: ZUnsetPrizeChoiceRequest,
      responses: {
        200: ZUnsetPrizeChoiceResponse,
        400: ZUnsetPrizeChoiceBadRequestResponse,
      },
      summary: "Unset player's prize choice for reselection",
    },

    unsetPrizeChoices: {
      method: 'POST',
      path: '/unset-prize-choices',
      body: ZUnsetPrizeChoicesRequest,
      responses: {
        200: ZUnsetPrizeChoicesResponse,
        400: ZUnsetPrizeChoiceBadRequestResponse,
      },
      summary: "Unset player's prize choice for reselection",
    },

    resendPrizeWinEmail: {
      method: 'POST',
      path: '/resend-prize-win-email',
      body: z.object({
        prizeWinId: z.string(),
      }),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
      summary:
        'Resend the prize win notification email for a specific prize win',
    },

    submitPrizeChoice: {
      method: 'POST',
      path: '/submit-prize-choice',
      body: ZAdminPrizeChoiceRequest,
      responses: {
        200: z.object({
          prizeOrderId: z.string(),
          prizeOrderProductIds: z.string().array(),
        }),
        400: c.type<{ message: string }>(),
      },
      summary: 'Submit a prize choice on behalf of a player',
    },

    amendPrizeChoice: {
      method: 'POST',
      path: '/amend-prize-choice',
      body: ZAdminPrizeChoiceRequest,
      responses: {
        200: z.object({
          prizeOrderId: z.string(),
          prizeOrderProductIds: z.string().array(),
        }),
        400: c.type<{ message: string }>(),
      },
      summary:
        'Cancel the current prize choice and submit a new one on behalf of a player',
    },

    getWinnerManagers: {
      method: 'GET',
      path: '/get-winner-managers',
      responses: {
        200: ZGetWinnerManagerResponse,
      },
    },
  },
  {
    pathPrefix: '/admin',
    baseHeaders: z.object({
      'x-access-token': z.string(),
    }),
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
      // 500: c.otherResponse({
      //   contentType: 'text/plain',
      //   body: z.literal('Server Error'),
      // }),
    },
  },
);
